import { useFormik } from "formik";
import { useEffect, useState } from "react";
import * as yup from "yup";
import { Link, useNavigate, useParams } from "react-router-dom";
import BaseButton from "Components/Base/BaseButton";
import BaseInput from "Components/Base/BaseInput";
import Loader from "Components/Base/BaseLoader";
import { toast } from "react-toastify";
import { checkStatusCodeSuccess } from "Components/constants/Common";
import {
  emailRegex,
  fileLimitErrorMessage,
  fileTypePDFErrorMessage,
  InputPlaceHolder,
  numberRegex,
  SelectPlaceHolder,
  validationMessages,
  zipcodeRegex,
} from "Components/constants/validation";
import { BaseSelect, MultiSelect } from "Components/Base/BaseSelect";
import {
  fileUpload,
  listOfBank,
  listOfCity,
  listOfCountry,
  listOfState,
} from "Api/common";
import { BaseImageURL } from "Api/services";
import {
  AadharCardLabel,
  AccountHolderNameLabel,
  AccountNumberKey,
  AccountNumberLabel,
  AddressLabel,
  AlternatePhoneNumberLabel,
  BankNameLabel,
  CityLabel,
  CountryLabel,
  DrivingLicenseLabel,
  EmailLabel,
  IFSCLabel,
  PhoneNumberLabel,
  PostalCodeLabel,
  ReAccountNumberLabel,
  StateLabel,
  StreetLabel,
  ViewFileLabel,
} from "Components/constants/Staff";
import {
  cancelLabel,
  submitLabel,
  updateLabel,
} from "Components/constants/Role";
import {
  EDIT_TECHNICIAN_LABEL,
  EXPERTISE_LABEL,
  NEW_TECHNICIAN_LABEL,
  TECHNICIAN_EXPERTISE,
  TECHNICIAN_NAME,
} from "Components/constants/Technician";
import {
  createTechnician,
  listOfExpertiseForSelectApi,
  updateTechnician,
  viewTechnician,
} from "Api/Technician";

const validationSchema = yup.object({
  technicianName: yup
    .string()
    .required(validationMessages.required(TECHNICIAN_NAME)),
  expertises: yup
    .array()
    .min(1, validationMessages.atLeastOneSelected(TECHNICIAN_EXPERTISE))
    .required(validationMessages.required(TECHNICIAN_EXPERTISE)),
  phoneNumber: yup
    .string()
    .required(validationMessages.required(PhoneNumberLabel))
    .matches(
      numberRegex,
      validationMessages.contactLength(PhoneNumberLabel, 10)
    ),
  alternatePhone: yup
    .string()
    .matches(
      numberRegex,
      validationMessages.contactLength(AlternatePhoneNumberLabel, 10)
    ),
  email: yup
    .string()
    .required(validationMessages.required(EmailLabel))
    .matches(emailRegex, validationMessages.format(EmailLabel)),
  accountHolderName: yup.string(),
  bankName: yup.string(),
  accountNumber: yup
    .string()
    .min(10, validationMessages.minLength(AccountNumberLabel, 10))
    .max(16, validationMessages.maxLength(AccountNumberLabel, 16)),
  reenterAccountNumber: yup
    .string()
    .oneOf(
      [yup.ref(AccountNumberKey)],
      validationMessages.passwordsMatch(
        AccountNumberLabel,
        ReAccountNumberLabel
      )
    )
    .min(10, validationMessages.minLength(ReAccountNumberLabel, 10))
    .max(16, validationMessages.maxLength(ReAccountNumberLabel, 16)),
  ifsc: yup
    .string()
    .min(11, validationMessages.maxChar(IFSCLabel, 11))
    .max(11, validationMessages.maxChar(IFSCLabel, 11)),
  shippingPostalCode: yup
    .string()
    .matches(
      zipcodeRegex,
      validationMessages.contactLength(PostalCodeLabel, 6)
    ),
});

const AddTechnician = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  document.title = id ? EDIT_TECHNICIAN_LABEL : NEW_TECHNICIAN_LABEL;
  const [loader, setLoader] = useState(false);
  const [viewTechnicianData, setViewTechnicianData] = useState<any>([]);
  const [btnLoader, setBtnLoader] = useState(false);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [city, setCity] = useState([]);
  const [expertiseList, setExpertiseList] = useState([]);
  const [selectedMulti, setSelectedMulti] = useState<any>("");
  const [bank, setBank] = useState([]);

  const fetchCountry = () => {
    // setLoader(true);
    let data;
    const payload = {};
    listOfCountry(payload)
      .then((res) => {
        if (checkStatusCodeSuccess(res?.statusCode)) {
          data = res?.data;
          setCountries(
            data?.map((item: any) => ({
              value: item?.id,
              label: item?.country_name,
              id: item?.id,
            }))
          );
        } else {
          toast.error(res?.message);
        }
      })
      .catch((err) => {
        return err;
      })
      .finally(() => {
        // setLoader(false);
      });
  };

  const fetchState = (id: number) => {
    // setLoader(true);
    let data;
    const payload = {
      condition: {
        country_id: id,
      },
    };
    listOfState(payload)
      .then((resp) => {
        if (checkStatusCodeSuccess(resp?.statusCode)) {
          data = resp?.data;
          setStates(
            data?.map((item: any) => ({
              value: item?.id,
              label: item?.state_name,
              id: item?.id,
            }))
          );
        } else {
          toast.error(resp?.message);
        }
      })
      .catch((err: any) => {
        setStates([]);
        fetchCity(null);
        return err;
      })
      .finally(() => {
        // setLoader(false);
      });
  };

  const fetchCity = (id: any) => {
    // setLoader(true);
    let data;
    const payload = {
      condition: {
        state_id: id,
      },
    };
    listOfCity(payload)
      .then((resp) => {
        if (checkStatusCodeSuccess(resp?.statusCode)) {
          data = resp?.data;
          setCity(
            data?.map((item: any) => ({
              value: item?.id,
              label: item?.city_name,
              id: item?.id,
            }))
          );
        } else {
          toast.error(resp?.message);
        }
      })
      .catch((err) => {
        setCity([]);
        return err;
      })
      .finally(() => {
        // setLoader(false);
      });
  };

  const fetchBank = () => {
    // setLoader(true);
    let data;
    listOfBank({})
      .then((resp) => {
        if (checkStatusCodeSuccess(resp?.statusCode)) {
          data = resp?.data;
          setBank(
            data?.map((item: any) => ({
              id: item?.id,
              label: item?.bank_name,
              value: item?.id,
            }))
          );
        }
      })
      .catch((err) => {
        setBank([]);
        return err;
      })
      .finally(() => {
        // setLoader(false);
      });
  };

  function handleMulti(selectedMulti: any) {
    const ids =
      selectedMulti?.length > 0
        ? selectedMulti?.map((item: any) => {
            return item.value;
          })
        : null;

    formik.setFieldValue("expertises", ids);
    setSelectedMulti(selectedMulti);
  }

  const fetchExpertiseSelect = () => {
    setLoader(true);
    let data;
    listOfExpertiseForSelectApi({})
      .then((res) => {
        if (checkStatusCodeSuccess(res?.statusCode)) {
          data = res?.data;
          setExpertiseList(
            data?.map((item: any) => ({
              value: item?.id,
              label: item?.expertise_name,
              id: item?.id,
            }))
          );
        } else {
          toast.error(res?.message);
        }
      })
      .catch((err) => {
        setExpertiseList([]);
        return err;
      })
      .finally(() => {
        setLoader(false);
      });
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      technicianName: id ? viewTechnicianData?.technician_name : "",
      expertises: id
        ? viewTechnicianData?.technicianExpertise?.map(
            (item: { expertise: { id: any } }) => {
              return item.expertise.id;
            }
          )
        : [],
      phoneNumber: id ? viewTechnicianData?.contact_no || "" : "",
      email: id ? viewTechnicianData?.email || "" : "",
      alternatePhone: id
        ? viewTechnicianData?.alternative_contact_no || ""
        : "",
      aadharCard: id ? viewTechnicianData?.aadhar_card || "" : "",
      drivingLicense: id ? viewTechnicianData?.driving_license || "" : "",
      accountHolderName: id
        ? viewTechnicianData?.bankDetail?.account_holder_name || ""
        : "",
      bankName: id ? viewTechnicianData?.bankDetail?.bank_id || "" : "",
      accountNumber: id
        ? viewTechnicianData?.bankDetail?.account_number || ""
        : "",
      reenterAccountNumber: id
        ? viewTechnicianData?.bankDetail?.account_number || ""
        : "",
      ifsc: id ? viewTechnicianData?.bankDetail?.ifsc_code || "" : "",
      shippingStreet: id ? viewTechnicianData?.address?.address || "" : "",
      shippingCountry: id ? viewTechnicianData?.address?.country_id : "",
      shippingState: id ? viewTechnicianData?.address?.state_id : "",
      shippingCity: id ? viewTechnicianData?.address?.city_id : "",
      shippingPostalCode: id
        ? viewTechnicianData?.address?.postal_code || ""
        : "",
    },
    validationSchema,
    onSubmit: (values) => {
      setBtnLoader(true);
      const payload = {
        technician_name: values.technicianName,
        expertise: values.expertises,
        contact_no: String(values.phoneNumber),
        email: values.email,
        alternative_contact_no: String(values.alternatePhone),
        aadhar_card: values.aadharCard,
        driving_license: values.drivingLicense,
        technicianBankDetail: {
          bank_id: values.bankName || null,
          account_holder_name: values.accountHolderName || null,
          account_number: values.accountNumber || null,
          ifsc_code: values.ifsc || null,
        },
        technicianAddress: {
          address: values.shippingStreet || null,
          country_id: values.shippingCountry || null,
          state_id: values.shippingState || null,
          city_id: values.shippingCity || null,
          postal_code: Number(values.shippingPostalCode) || null,
        },
      };
      if (id) {
        updateTechnician(Number(id), payload)
          .then((res) => {
            if (checkStatusCodeSuccess(res?.statusCode)) {
              toast.success(res?.message);
              navigate("/technician");
            } else {
              toast.error(res?.message);
            }
          })
          .catch((err) => {
            toast.error(err?.response?.data?.message || err?.message);
            return err;
          })
          .finally(() => {
            setBtnLoader(false);
          });
      } else {
        createTechnician(payload)
          .then((res) => {
            if (checkStatusCodeSuccess(res?.statusCode)) {
              toast.success(res?.message);
              navigate("/technician");
            } else {
              toast.error(res?.message);
            }
          })
          .catch((err) => {
            toast.error(err?.response?.data?.message || err?.message);
            return err;
          })
          .finally(() => {
            setBtnLoader(false);
          });
      }
    },
  });

  const handleFileUpload = (file: any, name: string) => {
    setLoader(true);
    const payload = new FormData();
    payload.append("files", file);
    fileUpload(payload)
      .then((resp) => {
        if (checkStatusCodeSuccess(resp?.StatusCodes)) {
          formik.setFieldValue(name, String(resp?.data[0]));
        } else {
          toast.error(resp?.message);
        }
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message || err?.message);
      })
      .finally(() => {
        setLoader(false);
      });
  };

  const handleFileChange = (event: any, name: string) => {
    const fileInput = event.target;
    const file = fileInput?.files[0];
    const maxSizeInBytes = 1024 * 1024;

    if (file && file.size > maxSizeInBytes) {
      toast.error(fileLimitErrorMessage);
      fileInput.value = "";
      return;
    }

    if (file && file.type !== "application/pdf") {
      toast.error(fileTypePDFErrorMessage);
      fileInput.value = "";
      return;
    }

    if (file) {
      handleFileUpload(file, name);
    }
  };

  useEffect(() => {
    if (formik.isSubmitting && Object.keys(formik.errors).length > 0) {
      const firstErrorKey = Object.keys(formik.errors)[0];

      if (firstErrorKey) {
        const errorElement: HTMLElement | null = document.querySelector(
          `[name="${firstErrorKey}"], #${firstErrorKey}`
        );

        if (errorElement) {
          errorElement.scrollIntoView({ behavior: "smooth", block: "center" });
          if (
            errorElement.tagName === "INPUT" ||
            errorElement.tagName === "SELECT"
          ) {
            errorElement.focus();
          } else {
            const selectControl = errorElement.closest(".select__control");
            if (selectControl) {
              selectControl.scrollIntoView({
                behavior: "smooth",
                block: "center",
              });
              const selectInput = selectControl.querySelector("input, select");
              if (selectInput) {
                (selectInput as HTMLElement).focus();
              }
            }
          }
        }
      }
    }
  }, [formik.isSubmitting, formik.errors]);

  const fetchTechnicianData = () => {
    if (id) {
      setLoader(true);
      viewTechnician(Number(id))
        .then((res) => {
          if (checkStatusCodeSuccess(res?.statusCode)) {
            const technicianData = res?.data;
            setViewTechnicianData(technicianData);
          } else {
            toast.error(res?.message);
          }
        })
        .catch((error) => {
          return error;
        })
        .finally(() => {
          setLoader(false);
        });
    }
  };

  useEffect(() => {
    if (viewTechnicianData?.address?.country_id) {
      fetchState(Number(viewTechnicianData?.address?.country_id));
    }
    if (viewTechnicianData?.address?.state_id) {
      fetchCity(Number(viewTechnicianData?.address?.state_id));
    }
    if (viewTechnicianData) {
      let technicianExpertises: any[] = [];
      expertiseList.forEach((item: any) => {
        viewTechnicianData?.technicianExpertise?.map((value: any) => {
          if (item?.id === value?.expertise?.id) {
            technicianExpertises.push(item);
          }
        });
      });
      handleMulti(technicianExpertises);
    }
  }, [viewTechnicianData]);

  useEffect(() => {
    if (id) {
      fetchTechnicianData();
    }
    fetchCountry();
    fetchExpertiseSelect();
    fetchBank();
  }, []);

  return (
    <div className="page-content">
      {loader && <Loader />}
      <div className="flex-grow-1 mx-3">
        <div className="mb-1 mx-2 d-flex justify-content-between align-items-center">
          <span className="fs-4">
            {id ? EDIT_TECHNICIAN_LABEL : NEW_TECHNICIAN_LABEL}
          </span>
          <button
            className="btn btn-danger"
            onClick={() => navigate("/technician")}
          >
            <i className="mdi mdi-arrow-left" />
          </button>
        </div>
        <div className="card word-wrap">
          <div className="card-body">
            <form onSubmit={formik.handleSubmit} className="mt-2 mb-4">
              <div className="row">
                <div className="col-md-3 col-sm-6 col-12">
                  <BaseInput
                    name="technicianName"
                    label={TECHNICIAN_NAME}
                    placeholder={InputPlaceHolder(TECHNICIAN_NAME)}
                    value={formik.values.technicianName}
                    onChange={formik.handleChange}
                    handleBlur={formik.handleBlur}
                    error={formik.errors.technicianName}
                    touched={formik.touched.technicianName}
                    required
                  />
                </div>
                <div className="col-md-3 col-sm-6 col-12">
                  <MultiSelect
                    name="expertises"
                    className="select-border"
                    label={EXPERTISE_LABEL}
                    isMulti={true}
                    options={expertiseList}
                    required
                    onChange={handleMulti}
                    handleBlur={formik.handleBlur}
                    error={formik.errors.expertises}
                    touched={formik.touched.expertises}
                    placeholder={SelectPlaceHolder(EXPERTISE_LABEL)}
                    value={selectedMulti || null}
                    isDisabled={false}
                  />
                </div>
                <div className="col-md-3 col-sm-6 col-12">
                  <BaseInput
                    name="phoneNumber"
                    type="number"
                    label={PhoneNumberLabel}
                    placeholder={InputPlaceHolder(PhoneNumberLabel)}
                    value={formik.values.phoneNumber}
                    onChange={formik.handleChange}
                    handleBlur={formik.handleBlur}
                    error={formik.errors.phoneNumber}
                    maxLength={10}
                    touched={formik.touched.phoneNumber}
                    required
                  />
                </div>
                <div className="col-md-3 col-sm-6 col-12">
                  <BaseInput
                    name="email"
                    label={EmailLabel}
                    placeholder={InputPlaceHolder(EmailLabel)}
                    type="email"
                    value={formik.values.email}
                    onChange={formik.handleChange}
                    handleBlur={formik.handleBlur}
                    error={formik.errors.email}
                    touched={formik.touched.email}
                    required
                  />
                </div>
              </div>
              <div className="row mt-3">
                <div className="col-md-3 col-sm-6 col-12">
                  <BaseInput
                    name="alternatePhone"
                    type="number"
                    maxLength={10}
                    label={AlternatePhoneNumberLabel}
                    placeholder={InputPlaceHolder(AlternatePhoneNumberLabel)}
                    value={formik.values.alternatePhone}
                    onChange={formik.handleChange}
                    handleBlur={formik.handleBlur}
                    error={formik.errors.alternatePhone}
                    touched={formik.touched.alternatePhone}
                  />
                </div>
                <div className="col-md-3 col-sm-6 col-12">
                  <BaseInput
                    name="aadharCard"
                    label={AadharCardLabel}
                    type="file"
                    tooltip={true}
                    tooltipIcon="help-circle-outline"
                    tooltipIconColor="info"
                    accept="application/pdf"
                    tooltipText="Only .pdf file is allowed"
                    onChange={(e) => handleFileChange(e, "aadharCard")}
                    handleBlur={formik.handleBlur}
                    error={formik.errors.aadharCard}
                    touched={formik.touched.aadharCard}
                  />
                  <div>
                    {formik.values.aadharCard && (
                      <span className="fw-normal">
                        <Link
                          target="_blank"
                          className="text-link font-weight-bold"
                          to={`${BaseImageURL}${formik.values.aadharCard}`}
                        >
                          {ViewFileLabel}
                        </Link>
                      </span>
                    )}
                  </div>
                </div>
                <div className="col-md-3 col-sm-6 col-12">
                  <BaseInput
                    name="drivingLicense"
                    label={DrivingLicenseLabel}
                    type="file"
                    tooltip={true}
                    tooltipIcon="help-circle-outline"
                    tooltipIconColor="info"
                    tooltipText="Only .pdf file is allowed"
                    onChange={(e) => handleFileChange(e, "drivingLicense")}
                    handleBlur={formik.handleBlur}
                    error={formik.errors.drivingLicense}
                    touched={formik.touched.drivingLicense}
                  />
                  <div>
                    {formik.values.drivingLicense && (
                      <span className="fw-normal">
                        <Link
                          target="_blank"
                          className="text-link font-weight-bold"
                          to={`${BaseImageURL}${formik.values.drivingLicense}`}
                        >
                          {ViewFileLabel}
                        </Link>
                      </span>
                    )}
                  </div>
                </div>
                <div className="col-md-3 col-sm-6 col-12">
                  <BaseInput
                    name="accountHolderName"
                    label={AccountHolderNameLabel}
                    placeholder={InputPlaceHolder(AccountHolderNameLabel)}
                    type="text"
                    value={formik.values.accountHolderName}
                    onChange={formik.handleChange}
                    handleBlur={formik.handleBlur}
                    error={formik.errors.accountHolderName}
                    touched={formik.touched.accountHolderName}
                  />
                </div>
              </div>
              <div className="row mt-3">
                <div className="col-md-3 col-sm-6 col-12">
                  <BaseSelect
                    name="bankName"
                    label={BankNameLabel}
                    options={bank}
                    value={formik.values.bankName}
                    placeholder={SelectPlaceHolder(BankNameLabel)}
                    handleChange={(field: any, value: any) => {
                      formik.setFieldValue(field, value);
                    }}
                    handleBlur={formik.handleBlur}
                    error={formik.errors.bankName}
                    touched={formik.touched.bankName}
                  />
                </div>
                <div className="col-md-3 col-sm-6 col-12">
                  <BaseInput
                    name="accountNumber"
                    label={AccountNumberLabel}
                    type="number"
                    placeholder={InputPlaceHolder(AccountNumberLabel)}
                    value={formik.values.accountNumber}
                    onChange={formik.handleChange}
                    handleBlur={formik.handleBlur}
                    error={formik.errors.accountNumber}
                    touched={formik.touched.accountNumber}
                  />
                </div>
                <div className="col-md-3 col-sm-6 col-12">
                  <BaseInput
                    name="reenterAccountNumber"
                    type="number"
                    label={ReAccountNumberLabel}
                    placeholder={InputPlaceHolder(ReAccountNumberLabel)}
                    value={formik.values.reenterAccountNumber}
                    onChange={formik.handleChange}
                    handleBlur={formik.handleBlur}
                    error={formik.errors.reenterAccountNumber}
                    touched={formik.touched.reenterAccountNumber}
                  />
                </div>
                <div className="col-md-3 col-sm-6 col-12">
                  <BaseInput
                    name="ifsc"
                    label={IFSCLabel}
                    placeholder={InputPlaceHolder(IFSCLabel)}
                    type="text"
                    value={formik.values.ifsc}
                    onChange={formik.handleChange}
                    handleBlur={formik.handleBlur}
                    error={formik.errors.ifsc}
                    touched={formik.touched.ifsc}
                  />
                </div>
              </div>
              <div className="row mt-4">
                <div className="col-12 fs-5">{AddressLabel}</div>
                <div className="col-md-3 col-sm-6 col-12 mt-3">
                  <BaseInput
                    name="shippingStreet"
                    type="textarea"
                    label={StreetLabel}
                    placeholder={InputPlaceHolder(StreetLabel)}
                    value={formik.values.shippingStreet}
                    onChange={formik.handleChange}
                    handleBlur={formik.handleBlur}
                    error={formik.errors.shippingStreet}
                    touched={formik.touched.shippingStreet}
                  />
                </div>
                <div className="col-md-3 col-sm-6 col-12 mt-3">
                  <BaseSelect
                    name="shippingCountry"
                    label={CountryLabel}
                    options={countries}
                    value={formik.values.shippingCountry}
                    placeholder={SelectPlaceHolder(CountryLabel)}
                    handleChange={(field: any, value: any) => {
                      formik.setFieldValue(field, value);
                      fetchState(value);
                    }}
                    handleBlur={formik.handleBlur}
                    error={formik.errors.shippingCountry}
                    touched={formik.touched.shippingCountry}
                  />
                </div>
                <div className="col-md-3 col-sm-6 col-12 mt-3">
                  <BaseSelect
                    name="shippingState"
                    label={StateLabel}
                    options={states}
                    value={formik.values.shippingState}
                    placeholder={SelectPlaceHolder(StateLabel)}
                    handleChange={(field: any, value: any) => {
                      formik.setFieldValue(field, value);
                      fetchCity(value);
                    }}
                    handleBlur={formik.handleBlur}
                    error={formik.errors.shippingState}
                    touched={formik.touched.shippingState}
                  />
                </div>
                <div className="col-md-3 col-sm-6 col-12 mt-3">
                  <BaseSelect
                    name="shippingCity"
                    label={CityLabel}
                    options={city}
                    value={formik.values.shippingCity}
                    placeholder={SelectPlaceHolder(CityLabel)}
                    handleChange={(field: any, value: any) => {
                      formik.setFieldValue(field, value);
                    }}
                    handleBlur={formik.handleBlur}
                    error={formik.errors.shippingCity}
                    touched={formik.touched.shippingCity}
                  />
                </div>
                <div className="col-md-3 col-sm-6 col-12 mt-3">
                  <BaseInput
                    name="shippingPostalCode"
                    type="number"
                    label={PostalCodeLabel}
                    placeholder={InputPlaceHolder(PostalCodeLabel)}
                    value={formik.values.shippingPostalCode}
                    onChange={formik.handleChange}
                    handleBlur={formik.handleBlur}
                    error={formik.errors.shippingPostalCode}
                    touched={formik.touched.shippingPostalCode}
                  />
                </div>
              </div>

              <div className="row mt-3 d-flex justify-content-end">
                <div className="col-auto d-flex justify-content-end gap-2 text-end">
                  <BaseButton
                    type="submit"
                    label={id ? updateLabel : submitLabel}
                    color="success"
                    loader={btnLoader}
                    disable={btnLoader}
                  />
                  <BaseButton
                    type="button"
                    label={cancelLabel}
                    onClick={() => navigate("/technician")}
                    color="danger"
                  />
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddTechnician;
